import { z } from 'zod';
import { supabase } from '@/utils/supabase';
import { validatePaymentAmount } from '@/utils/compensation/paymentCalculations';
import { RECAPTURE_CHANGED_EVENT } from '@/services/recaptureService';

export const BONUS_AWARD_LABELS = {
  dollar_volume: 'LO of the Month — Dollar Volume',
  units: 'LO of the Month — Units'
} as const;

export interface ProfitabilityBonus {
  id: string;
  recipient_id: string;
  recipient_name: string | null;
  award_type: keyof typeof BONUS_AWARD_LABELS;
  award_month: string;
  amount: number;
  status: 'active' | 'voided';
  version: number;
  effective_date: string;
  /** Server Pacific business date, never the browser clock. */
  is_effective: boolean;
  history?: {
    id: string;
    event_type: 'created' | 'updated' | 'voided';
    version: number;
    actor_name: string | null;
    created_at: string;
    reason: string | null;
    before_state: Partial<ProfitabilityBonus> | null;
    after_state: Partial<ProfitabilityBonus>;
  }[];
}

export interface BonusReportMetadata {
  bonuses?: ProfitabilityBonus[];
  bonus_owner?: boolean;
  bonus_entry_enabled?: boolean;
  bonus_current_month?: string;
}

const inputSchema = z.object({
  id: z.string().uuid(),
  recipientId: z.string().uuid(),
  awardType: z.enum(['dollar_volume', 'units']),
  awardMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-01$/),
  amount: z.string(),
  expectedVersion: z.number().int().positive().nullable(),
  reason: z.string()
});
export type BonusInput = z.infer<typeof inputSchema>;
const AWARD_MONTH_ERROR = 'Choose an award month from July 2026 through the current Pacific month.';

export function validateBonusInput(input: BonusInput, currentMonth: string): number {
  const parsed = inputSchema.safeParse(input);
  if (!parsed.success) {
    // ZodError.message is a JSON issue dump; the dialog renders thrown messages verbatim.
    const field = parsed.error.issues[0]?.path[0];
    throw new Error(field === 'recipientId' ? 'Choose a loan officer.'
      : field === 'awardMonth' ? AWARD_MONTH_ERROR
      : 'Bonus details are invalid. Close this dialog and refresh the report.');
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])-01$/.test(currentMonth)) {
    throw new Error('Bonus dates are unavailable. Refresh the report.');
  }
  if (input.awardMonth < '2026-07-01' || input.awardMonth > currentMonth) {
    throw new Error(AWARD_MONTH_ERROR);
  }
  const money = validatePaymentAmount(input.amount);
  if (!money.valid || money.amount >= 10_000_000_000) {
    throw new Error('Enter a positive amount below $10 billion with at most two decimal places.');
  }
  if (input.expectedVersion !== null && !input.reason.trim()) {
    throw new Error('A correction reason is required.');
  }
  return money.amount;
}

export async function saveProfitabilityBonus(input: BonusInput, currentMonth: string): Promise<ProfitabilityBonus> {
  const amount = validateBonusInput(input, currentMonth);
  const { data, error } = await supabase.rpc('save_lo_profitability_bonus', {
    p_id: input.id,
    p_recipient_id: input.recipientId,
    p_award_type: input.awardType,
    p_award_month: input.awardMonth,
    p_amount: amount,
    p_expected_version: input.expectedVersion,
    p_reason: input.reason.trim() || null
  });
  if (error) {
    throw error;
  }
  window.dispatchEvent(new Event(RECAPTURE_CHANGED_EVENT));
  return data as ProfitabilityBonus;
}

export async function voidProfitabilityBonus(id: string, version: number, reason: string): Promise<ProfitabilityBonus> {
  z.string().uuid().parse(id);
  z.number().int().positive().parse(version);
  if (!reason.trim()) {
    throw new Error('A void reason is required.');
  }
  const { data, error } = await supabase.rpc('void_lo_profitability_bonus', {
    p_id: id, p_expected_version: version, p_reason: reason.trim()
  });
  if (error) {
    throw error;
  }
  window.dispatchEvent(new Event(RECAPTURE_CHANGED_EVENT));
  return data as ProfitabilityBonus;
}
